import React from 'react';

import {
  Datagrid,
  DateField,
  FunctionField,
  Loading,
  RecordContextProvider,
  ReferenceField,
  SingleFieldList,
  TextField,
  useGetOne,
  usePermissions,
  useRecordContext,
} from 'react-admin';

import { Grid } from '@mui/material';

import NoResults from '../layout/NoResults';
import DateDiffField from '../utils/auditTracks/DateDiffField';
import StatusDiffField from '../utils/auditTracks/StatusDiffField';
import ReferenceDiffField from '../utils/auditTracks/ReferenceDiffField';
import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import { ICase } from '../../utilities/schemas/case';
import ArrayDiffField from '../utils/auditTracks/ArrayDiffField';
import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';
import resourceNames from '../../constants/resourceNames';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import ChipStringField from '../../customFields/ChipStringField';
import ReferenceArrayDiffField from '../utils/auditTracks/ReferenceArrayDiffField';
import { PingListBody } from '../ping/PingList';
import actions, { Actions } from '../../constants/actions';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import hasAccess from '../../utilities/hasAccess';
import { CommentMessageField } from '../comment/CommentList';
import PermissionMissing from '../utils/PermissionMissing';

const RequestChipField = () => {
  const record = useRecordContext();

  const request = record?.request;

  return (
    <ChipStringField hasLink source={request?.customId ? 'customId' : 'id'} />
  );
};

const CaseAuditDetails = () => {
  const record = useRecordContext<AuditTrack<ICase>>();
  const { data, isLoading } = useGetOne<AuditTrack<ICase>>(`audit-tracks/${record?.id}/previous`, { id: '' });
  const { permissions } = usePermissions<Actions[]>();

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<ICase>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    createdAt: resourceData.createdAt?.hasChanged,
    closedAt: resourceData.closedAt?.hasChanged,
    status: resourceData.status?.hasChanged,
    mergedInto: resourceData.mergedInto?.hasChanged,
    entities: resourceData.entities?.hasChanged,
    pings: resourceData.pings?.hasChanged,
    requests: resourceData.requests?.hasChanged,
    files: resourceData.files?.hasChanged,
    assignmentMemberId: resourceData.assignment?.memberId?.hasChanged,
    comments: resourceData.comments?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.createdAt && (
            <DateDiffField source="createdAt" label="Created At" showTime />
          )}
          {hasFieldChanges.closedAt && (
            <DateDiffField source="closedAt" label="Closed At" showTime />
          )}
          {hasFieldChanges.status && (
            <StatusDiffField source="status" label="Status" resource={resourceNames.CASES} />
          )}
          {hasFieldChanges.mergedInto && (
            <ReferenceDiffField
              source="mergedInto"
              label="Merged Into"
              reference={resourceNames.CASES}
              optionText="id"
            >
              <TextField source="id" />
            </ReferenceDiffField>
          )}
          {hasFieldChanges.entities && (
            <ArrayDiffField source="entities" label="Entities">
              <SingleFieldList linkType={false}>
                <ReferenceEntityField source="">
                  <EntityDisplayNameField chip />
                </ReferenceEntityField>
              </SingleFieldList>
            </ArrayDiffField>
          )}
          {hasFieldChanges.requests && (
            <ArrayDiffField source="requests" label="Requests">
              <SingleFieldList linkType={false}>
                <FunctionField
                  render={(requestRecord) => (
                    <ReferenceField record={{ id: String(requestRecord) }} source="id" reference={resourceNames.REQUESTS} emptyText="-">
                      <RequestChipField />
                    </ReferenceField>
                  )}
                />
              </SingleFieldList>
            </ArrayDiffField>
          )}
          {hasFieldChanges.files && (
            <ArrayDiffField source="files" label="Files">
              <SingleFieldList linkType={false}>
                <ChipStringField source="name" />
              </SingleFieldList>
            </ArrayDiffField>
          )}
          {hasFieldChanges.assignmentMemberId && (
            <ReferenceDiffField
              source="assignment.memberId"
              label="Assignment member"
              reference={resourceNames.MEMBERS}
            >
              <FunctionField
                render={(member?: { firstName?: string; lastName?: string }) => `${member?.firstName ?? ''} ${member?.lastName ?? ''}`.trim() || '-'}
              />
            </ReferenceDiffField>
          )}
          {hasFieldChanges.comments && (
            <ArrayDiffField source="comments" label="Comments" width="100%" size={{ xs: 12 }}>
              <Datagrid sx={boldDataGridStyle} bulkActionButtons={false}>
                <CommentMessageField emptyText="-" label="Comment" />
                <DateField showTime source="date" />
                {hasAccess(permissions, actions.MEMBER_GET) ? (
                  <ReferenceField label="Author" source="member" reference={resourceNames.MEMBERS} link="edit">
                    <FunctionField render={(member: { firstName: string; lastName: string }) => `${member.firstName} ${member.lastName}`} />
                  </ReferenceField>
                ) : (<PermissionMissing />)}
              </Datagrid>
            </ArrayDiffField>
          )}
          {hasFieldChanges.pings && (
            <ReferenceArrayDiffField width="100%" size={{ xs: 12 }} source="pings" objectSource="id" label="Pings" reference={resourceNames.PINGS}>
              <PingListBody />
            </ReferenceArrayDiffField>
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default CaseAuditDetails;
